import React from 'react';
import { CartesianGrid, Legend, Tooltip, XAxis, YAxis } from 'recharts';
import { ImplicitLabelType } from 'recharts/types/component/Label';

import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';
import { VegaLiteSpec } from '@atom/types/conversationalAnalytics';

import useConversationalAnalyticsData from './hooks/useConversationalAnalyticsData';
import ConversationalAnalyticsChartTooltip from './ConversationalAnalyticsChartTooltip';

type ConversationalAnalyticsChartAxesProps = {
  spec: VegaLiteSpec;
  isHorizontal?: boolean;
};

const styles = {
  axis: {
    fontSize: fonts.md,
    fill: colors.neutral.dim,
  },
  tick: {
    fontSize: fonts.sm,
    fill: colors.neutral.dim,
  },
};

function getXAxisLabel(
  categoryTitle: string | undefined,
  valueTitle: string | undefined,
  isHorizontal: boolean,
): ImplicitLabelType {
  if (categoryTitle && !isHorizontal) {
    return {
      value: categoryTitle,
      position: 'insideBottom',
      offset: -20,
      style: styles.axis,
    };
  }

  if (valueTitle && isHorizontal) {
    return {
      value: valueTitle,
      position: 'insideBottom',
      offset: -20,
      style: styles.axis,
    };
  }

  return undefined;
}

function getYAxisLabel(
  valueTitle: string | undefined,
  isHorizontal: boolean,
): ImplicitLabelType {
  if (valueTitle && !isHorizontal) {
    return {
      value: valueTitle,
      angle: -90,
      position: 'left',
      style: { ...styles.axis, textAnchor: 'middle' },
    };
  }

  return undefined;
}

const ConversationalAnalyticsChartAxes: React.FC<
  ConversationalAnalyticsChartAxesProps
> = ({ spec, isHorizontal = false }) => {
  const { xDisplayField, yField, xTitle, yTitle, colorField } =
    useConversationalAnalyticsData(spec);

  const categoryTitle = isHorizontal ? yTitle : xTitle;
  const valueTitle = isHorizontal ? xTitle : yTitle;

  return (
    <>
      <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral.silver} />
      <XAxis
        type={isHorizontal ? 'number' : 'category'}
        dataKey={isHorizontal ? undefined : xDisplayField}
        label={getXAxisLabel(categoryTitle, valueTitle, isHorizontal)}
        tick={styles.tick as any}
      />
      <YAxis
        type={isHorizontal ? 'category' : 'number'}
        dataKey={isHorizontal ? yField : undefined}
        width={isHorizontal ? 160 : undefined}
        label={getYAxisLabel(valueTitle, isHorizontal)}
        tick={styles.tick as any}
      />
      <Tooltip
        content={({ active, payload }) => (
          <ConversationalAnalyticsChartTooltip
            active={active}
            payload={payload}
            xDisplayField={isHorizontal ? yField : xDisplayField}
          />
        )}
      />
      {colorField && spec.encoding.color?.legend !== null && <Legend />}
    </>
  );
};

export default ConversationalAnalyticsChartAxes;
